import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRightLeft, Boxes, Loader2, Search, Users as UsersIcon } from 'lucide-react';
import {
  api,
  type OrganizationMemberChanges,
  type OrganizationMemberFailure,
} from '../lib/api';
import { Drawer } from './ui/Drawer';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Badge } from './ui/Badge';
import { useOrgLookup } from '../lib/orgLookup';

export type MembershipKind = 'users' | 'applications';

/** Server-side cap in `updateOrganizationMembers`, mirrored so the UI can stop first. */
const MAX_CHANGES = 50;

/** How many candidates to render before asking the user to narrow the search. */
const MAX_LISTED = 100;

interface Candidate {
  id: string;
  label: string;
  sublabel: string | null;
  organizationId: string | null;
}

interface Props {
  kind: MembershipKind;
  organizationId: string;
  organizationName: string;
  onClose: () => void;
}

/**
 * Picks users or applications to attach to an organization. Detaching is a
 * per-row action on the organization page instead — removing something is a
 * decision about one record, and burying it in a checkbox list made it too easy
 * to do by accident.
 */
export function AddOrgMembersDrawer({ kind, organizationId, organizationName, onClose }: Props) {
  const queryClient = useQueryClient();
  const lookup = useOrgLookup();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const [failures, setFailures] = useState<OrganizationMemberFailure[]>([]);

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.listUsers(),
    enabled: kind === 'users',
  });
  const applications = useQuery({
    queryKey: ['applications'],
    queryFn: () => api.listApplications(),
    enabled: kind === 'applications',
  });
  const source = kind === 'users' ? users : applications;

  // Anything already here is not a candidate — this drawer only ever adds.
  const candidates = useMemo<Candidate[]>(() => {
    const rows: Candidate[] =
      kind === 'users'
        ? (users.data?.items ?? []).map((u) => ({
            id: u.id,
            label: u.username ?? '(no username)',
            sublabel: u.id,
            organizationId: u.organizationId ?? null,
          }))
        : (applications.data?.items ?? []).map((a) => ({
            id: a.id,
            label: a.displayName ?? a.application ?? '(unnamed application)',
            sublabel: [a.application, a.stack, a.x5serverIP].filter(Boolean).join(' · ') || a.id,
            organizationId: a.organizationId ?? null,
          }));
    return rows
      .filter((r) => r.organizationId !== organizationId)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [kind, users.data, applications.data, organizationId]);

  const matching = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return candidates;
    return candidates.filter(
      (c) =>
        c.label.toLowerCase().includes(needle) ||
        c.sublabel?.toLowerCase().includes(needle) ||
        c.id.toLowerCase().includes(needle),
    );
  }, [candidates, q]);

  const visible = matching.slice(0, MAX_LISTED);

  function toggle(id: string) {
    setFailures([]);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const moving = [...selected].filter(
    (id) => candidates.find((c) => c.id === id)?.organizationId != null,
  ).length;
  const overCap = selected.size > MAX_CHANGES;

  const save = useMutation({
    mutationFn: () => {
      const changes: OrganizationMemberChanges =
        kind === 'users'
          ? { addUserIds: [...selected] }
          : { addApplicationIds: [...selected] };
      return api.updateOrganizationMembers(organizationId, changes);
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['organization'] });
      void queryClient.invalidateQueries({ queryKey: [kind] });
      if (result.failed.length === 0) {
        onClose();
        return;
      }
      // Some landed and some did not. Keep only the failures selected so a
      // retry does not re-send what already worked.
      const stillPending = new Set(result.failed.map((f) => f.id));
      setSelected((prev) => new Set([...prev].filter((id) => stillPending.has(id))));
      setFailures(result.failed);
    },
  });

  const Icon = kind === 'users' ? UsersIcon : Boxes;
  const noun = kind === 'users' ? 'users' : 'applications';

  return (
    <Drawer
      open
      onClose={onClose}
      className="max-w-xl"
      title={
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Icon className="h-4 w-4 text-muted-foreground" />
            Add {noun}
          </h2>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">to {organizationName}</p>
        </div>
      }
      footer={
        <>
          <div className="mr-auto text-xs text-muted-foreground">
            {selected.size === 0 ? (
              'Nothing selected'
            ) : (
              <span className={overCap ? 'text-destructive' : undefined}>
                {selected.size} selected
                {moving > 0 && ` · ${moving} moving from another organization`}
                {overCap && ` · over the ${MAX_CHANGES} limit`}
              </span>
            )}
          </div>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={selected.size === 0 || overCap || save.isPending}
          >
            {save.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Adding…
              </>
            ) : (
              `Add ${selected.size || ''}`.trim()
            )}
          </Button>
        </>
      }
    >
      {source.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading {noun}…
        </div>
      )}
      {source.error && <p className="text-sm text-destructive">{(source.error as Error).message}</p>}

      {save.isError && (
        <div className="mb-4 flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Nothing was added</p>
            <p className="mt-1 text-xs">{(save.error as Error).message}</p>
          </div>
        </div>
      )}

      {failures.length > 0 && (
        <div className="mb-4 flex gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">{failures.length} could not be added — the rest were</p>
            <ul className="mt-1 space-y-0.5 text-xs opacity-90">
              {failures.map((f) => (
                <li key={f.id}>
                  <span className="font-mono">{f.id}</span> — {f.reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {source.data && (
        <>
          <div className="relative mb-3">
            <Search className="absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${noun}…`}
              className="pl-8"
              autoFocus
            />
          </div>

          {visible.length === 0 ? (
            <p className="px-1 text-xs text-muted-foreground">
              {candidates.length === 0
                ? `Every ${kind === 'users' ? 'user' : 'application'} is already in this organization.`
                : 'No matches.'}
            </p>
          ) : (
            <ul className="divide-y divide-line-soft">
              {visible.map((c) => (
                <li key={c.id}>
                  <label className="flex cursor-pointer items-center gap-2.5 px-1 py-2 transition-colors hover:bg-raise">
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggle(c.id)}
                      className="h-4 w-4 shrink-0 rounded border-border"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">{c.label}</span>
                      {c.sublabel && (
                        <span className="block truncate font-mono text-[11px] text-muted-foreground">
                          {c.sublabel}
                        </span>
                      )}
                    </span>
                    {c.organizationId && (
                      <Badge variant="outline" className="shrink-0 text-[10px]">
                        <ArrowRightLeft className="h-3 w-3" />
                        {lookup.resolveName(c.organizationId)}
                      </Badge>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          )}

          {matching.length > visible.length && (
            <p className="mt-2 px-1 text-xs text-muted-foreground">
              Showing {visible.length} of {matching.length} — narrow the search to see the rest.
            </p>
          )}
        </>
      )}
    </Drawer>
  );
}
